import { RoleLink } from "@/components/client-role";
import { type Item, KIND, KIND_ORDER, type Kind, buildItems, rankItems } from "@/components/dashboard/attention";
import { PageHeader } from "@/components/dashboard/page-header";
import { SectionLabel } from "@/components/dashboard/section-label";
import { EmptyState, Skeleton } from "@/components/dashboard/state";
import { clientApi } from "@/lib/client-api";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2 } from "lucide-react";

// The Needs attention page — its own destination in the nav (not a band, not a hidden tab). Re-cuts the
// live roles into the handful that need a decision, GROUPED by severity: a labelled section per kind (the
// shared section-label + card used across the product), with the items ranked oldest-first inside. The
// client never has a route here.

/** One work item: who + the ask on the left, its age and a chase arrow on the right. The group it sits in
 *  already carries the kind (its section label), so the row stays clean — no repeated glyph or label. */
function Row({ item }: { item: Item }) {
  const primary = item.person ? `${item.person} · ${item.role.title}` : item.role.title;
  return (
    <RoleLink
      to="/search/$searchId"
      params={{ searchId: item.role.id }}
      search={{ from: "attention" }}
      className="group flex items-center gap-4 px-4 py-3.5 outline-none transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{primary}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{item.ask}</p>
      </div>
      <span className="shrink-0 text-sm tabular-nums text-muted-foreground">{item.age}</span>
      <ArrowRight
        className="size-4 shrink-0 text-muted-foreground/40 transition-all group-hover:translate-x-0.5 group-hover:text-foreground"
        aria-hidden
      />
    </RoleLink>
  );
}

/** A severity group: the shared section label (kind + count) over a plain card of items — the SAME list
 *  UI used everywhere else in the product (Team, Activity), so the queue doesn't read as its own design.
 *  Severity is carried by a small semantic colour dot + colour-coded tally on the label and the group order
 *  (most urgent first) — colour always paired with the text label, never colour alone. */
function Group({ kind, items }: { kind: Kind; items: Item[] }) {
  const meta = KIND[kind];
  return (
    <section>
      <SectionLabel
        meta={
          <span className="text-xs font-medium tabular-nums" style={{ color: meta.color }}>
            {items.length}
          </span>
        }
      >
        <span className="inline-flex items-center gap-2">
          <span className="size-1.5 rounded-full" style={{ backgroundColor: meta.color }} aria-hidden />
          {meta.label}
        </span>
      </SectionLabel>
      <div className="overflow-hidden rounded-xl border bg-card">
        <div className="divide-y">
          {items.map((item) => (
            <Row key={`${item.role.id}-${item.ask}`} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function AttentionPage() {
  const { data, isError } = useQuery({ queryKey: ["client", "overview"], queryFn: () => clientApi.overview() });
  const roles = data ? Object.values(data.tiers).flat() : [];
  const items = rankItems(buildItems(roles));
  const groups = KIND_ORDER.map((k) => ({ k, items: items.filter((i) => i.kind === k) })).filter((g) => g.items.length);
  const loading = !data && !isError;

  return (
    <div className="animate-fade-up space-y-6">
      <PageHeader
        title="Needs attention"
        description="Where the portfolio needs a decision or a nudge — grouped by urgency, oldest first."
      >
        <span className="hidden items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground sm:inline-flex">
          <span className="size-1.5 rounded-full bg-[var(--brand-accent)]" aria-hidden />
          {items.length} open
        </span>
      </PageHeader>

      {isError ? (
        <EmptyState>Couldn't load the queue.</EmptyState>
      ) : loading ? (
        <div className="space-y-6">
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-28 w-full rounded-xl" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border bg-card px-4 py-16 text-center">
          <CheckCircle2 className="size-7 text-[var(--color-success)]" aria-hidden />
          <p className="text-base font-medium text-foreground">You're all caught up</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Nothing needs a decision right now — every live search is on track.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(({ k, items: groupItems }) => (
            <Group key={k} kind={k} items={groupItems} />
          ))}
        </div>
      )}

      <p className="px-1 text-xs text-muted-foreground">Representative queue — every item links through to its role.</p>
    </div>
  );
}
